import React, { useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Typography, Button, CircularProgress, Paper, InputAdornment, Stack, Box, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { AttachMoney } from "@mui/icons-material";
import client from "../../../../API";
import { useCreatorAuth } from "../../../../Hooks/creator-use-auth";
import { useMutation } from "react-query";
import useAlertDialog from "../../../../Components/useAlertDialog";
import AlertDialog from "../../../../Components/AlertDialog";

/* ───────── constants ───────── */
const APP_SHELL_HEIGHT = 86; // Must match BAR_HEIGHT from AppShell
const EXTRA_GUTTER = 12;

const CreatorNegotiateDialog = ({
  open,
  handleClose,
  campaignId,
  currentRate,
  refreshCampaign
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { creatorToken } = useCreatorAuth();
  const [counterOffer, setCounterOffer] = useState('');
  const { dialogState, openDialog: openAlert, closeDialog } = useAlertDialog();
  const netRate = counterOffer && !isNaN(parseFloat(counterOffer)) ? parseFloat(counterOffer).toFixed(2) : "0.00";

  const { mutate: submitNegotiation, isLoading: isSubmitting } = useMutation(
    (offer) => client.creatorConnect.campaignNegotiate(campaignId, { 
      counterOffer: parseFloat(offer),
      creatorId: creatorToken.creator_user.username
    }),
    {
      onSuccess: () => {
        openAlert(
          'Counter Offer Submitted',
          'Your counter offer has been submitted. The brand will review your proposal.',
          () => {
            refreshCampaign();
            handleClose();
            closeDialog();
          },
          null,
          'Ok',
          null
        );
      },
      onError: (error) => {
        openAlert(
          "Error",
          `Error submitting counter offer: ${error.response?.data?.error || error.message}`,
          closeDialog,
          null,
          "Ok",
          null
        );
      }
    }
  );

  const handleSubmit = () => {
    if (!counterOffer || isNaN(counterOffer) || parseFloat(counterOffer) <= 0) {
      openAlert('Invalid Offer', 'Please enter a valid offer amount', closeDialog, null, 'Ok', null);
      return;
    }
    submitNegotiation(counterOffer);
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
        sx={{
          '& .MuiDialog-paper': {
            margin: isMobile ? 0 : theme.spacing(2),
            height: isMobile ? '100vh' : 'auto',
            maxHeight: isMobile ? '100vh' : '90vh',
            borderRadius: 0,
            paddingTop: isMobile ? 'env(safe-area-inset-top, 0px)' : 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }
        }}
        PaperProps={{
          sx: {
            boxShadow: isMobile ? theme.shadows[8] : theme.shadows[24],
            border: `1px solid ${theme.palette.divider}`,
            WebkitOverflowScrolling: 'touch',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '100%',
            backgroundColor: theme.palette.background.paper,
          }
        }}
      >
        {/* Sticky Header */}
        <Paper 
          elevation={0}
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
            borderRadius: 0,
            overflow: 'hidden',
          }}
        >
          <DialogTitle
            sx={{
              background: isMobile 
                ? `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})` 
                : `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
              color: theme.palette.common.white,
              py: isMobile ? 3 : 3.5,
              borderRadius: 0,
              borderBottom: `1px solid ${theme.palette.primary.dark}`,
              boxShadow: `0 2px 8px ${theme.palette.primary.main}25`,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: 0,
                  p: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <AttachMoney 
                  fontSize={isMobile ? "large" : "medium"} 
                  sx={{ fontSize: isMobile ? '1.75rem' : '1.5rem' }}
                />
              </Box>
              <Typography 
                variant="h6" 
                fontWeight="700"
                sx={{
                  fontSize: isMobile ? '1.3rem' : '1.5rem',
                  letterSpacing: '0.02em',
                }}
              >
                Submit Counter Offer
              </Typography>
            </Stack>
          </DialogTitle>
        </Paper>
        
        {/* Scrollable Content */}
        <DialogContent 
          dividers={false}
          sx={{ 
            p: isMobile ? 3 : 4,
            flex: 1,
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            minHeight: 0,
            backgroundColor: theme.palette.background.default,
          }}
        >
          <Stack spacing={isMobile ? 3 : 4}>
            {/* Current Offer Display */}
            <Box sx={{ 
              background: `linear-gradient(135deg, ${theme.palette.grey[50]}, ${theme.palette.grey[100]})`,
              borderRadius: 0, 
              p: isMobile ? 3 : 3.5,
              border: `2px solid ${theme.palette.primary.light}`,
              textAlign: 'center',
              boxShadow: `0 4px 20px ${theme.palette.primary.main}15`,
            }}>
              <Typography 
                variant="subtitle1" 
                color="text.secondary"
                sx={{
                  fontSize: isMobile ? '1rem' : '0.875rem',
                  mb: 1,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                }}
              >
                Current Offer
              </Typography>
              <Typography 
                variant="h3" 
                fontWeight="800" 
                color="primary"
                sx={{
                  fontSize: isMobile ? '2.8rem' : '2.5rem',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                ${currentRate}
              </Typography>
            </Box>

            {/* Counter Offer Input */}
            <TextField
              fullWidth
              label="Counter Offer Amount"
              variant="outlined"
              type="number"
              value={counterOffer}
              onChange={(e) => setCounterOffer(e.target.value.replace(/[^0-9.]/g, ''))}
              inputProps={{ 
                min: 0, 
                step: 0.01,
                inputMode: 'decimal',
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Box
                      sx={{
                        backgroundColor: theme.palette.primary.light,
                        borderRadius: 0,
                        p: 0.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <AttachMoney 
                        color="primary" 
                        sx={{ fontSize: isMobile ? '1.5rem' : '1.25rem' }}
                      />
                    </Box>
                  </InputAdornment>
                ),
                sx: { 
                  fontSize: isMobile ? '1.3rem' : '1.2rem', 
                  fontWeight: 600,
                  minHeight: isMobile ? 64 : 56,
                  borderRadius: 0,
                  '& fieldset': {
                    borderWidth: 2,
                    borderColor: theme.palette.primary.light,
                  },
                  '&:hover fieldset': {
                    borderColor: theme.palette.primary.main,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.primary.main,
                    borderWidth: 2,
                  }
                }
              }}
              InputLabelProps={{
                sx: {
                  fontSize: isMobile ? '1.1rem' : '1rem',
                  fontWeight: 600,
                }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 0,
                  backgroundColor: theme.palette.background.paper,
                  boxShadow: `0 2px 8px ${theme.palette.grey[300]}50`,
                }
              }}
            />

            <Box sx={{ height: 16 }} />

            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center',
                p: isMobile ? 3 : 2.5,
                borderRadius: 0,
                background: `linear-gradient(135deg, ${theme.palette.success.light}, ${theme.palette.success.main}15)`,
                border: `2px solid ${theme.palette.success.main}`,
                boxShadow: `0 4px 20px ${theme.palette.success.main}25`,
              }}>
                <Typography 
                  variant="h6" 
                  fontWeight="700"
                  sx={{
                    fontSize: isMobile ? '1.2rem' : '1.1rem',
                    color: theme.palette.success.dark,
                  }}
                >
                  Your Net Rate
                </Typography>
                <Typography 
                  variant="h5" 
                  fontWeight="800" 
                  sx={{
                    fontSize: isMobile ? '1.6rem' : '1.4rem',
                    background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  ${netRate}
                </Typography>
              </Box>
          </Stack>

          <Box sx={{ height: isMobile ? 20 : 12 }} />
        </DialogContent>

        {/* Sticky Footer with Actions */}
        <Paper 
          elevation={0}
          sx={{
            position: 'sticky',
            bottom: 0,
            zIndex: 1,
            borderTop: `1px solid ${theme.palette.divider}`,
            borderRadius: 0,
            paddingBottom: isMobile 
              ? `max(${APP_SHELL_HEIGHT + EXTRA_GUTTER}px, calc(env(safe-area-inset-bottom, 0px) + ${APP_SHELL_HEIGHT + EXTRA_GUTTER}px))`
              : 0,
            overflow: 'hidden',
            backgroundColor: theme.palette.background.paper,
            boxShadow: `0 -4px 20px ${theme.palette.grey[300]}30`,
          }}
        >
          <DialogActions 
            sx={{ 
              px: isMobile ? 3 : 4, 
              py: isMobile ? 3 : 2.5, 
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <Stack 
              direction={isMobile ? "column" : "row"} 
              spacing={isMobile ? 2 : 2} 
              width="100%"
            >
              <Button 
                onClick={handleClose} 
                variant="outlined" 
                color="inherit"
                fullWidth
                sx={{ 
                  borderRadius: 0,
                  py: isMobile ? 2 : 1.5,
                  minHeight: 48,
                  fontSize: isMobile ? '1.05rem' : '0.95rem',
                  fontWeight: '700',
                  borderWidth: 2,
                  borderColor: theme.palette.grey[400],
                  color: theme.palette.text.primary,
                  '&:hover': { 
                    borderWidth: 2,
                    borderColor: theme.palette.grey[600],
                    backgroundColor: theme.palette.grey[50],
                  }
                }}
              >
                Cancel
              </Button>
              <Button 
                variant="contained" 
                color="primary"
                onClick={handleSubmit}
                disabled={isSubmitting}
                fullWidth
                sx={{ 
                  borderRadius: 0,
                  py: isMobile ? 2 : 1.5,
                  minHeight: 48,
                  fontSize: isMobile ? '1.05rem' : '0.95rem',
                  fontWeight: '700',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  boxShadow: `0 4px 16px ${theme.palette.primary.main}40`,
                  '&:hover': { 
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                    boxShadow: `0 6px 20px ${theme.palette.primary.main}50`,
                  },
                  '&:disabled': {
                    backgroundColor: theme.palette.action.disabledBackground,
                    boxShadow: 'none',
                  }
                }}
              >
                {isSubmitting ? (
                  <CircularProgress 
                    size={isMobile ? 28 : 24} 
                    sx={{ color: theme.palette.common.white }} 
                  />
                ) : (
                  'Submit Offer'
                )}
              </Button>
            </Stack>
          </DialogActions>
        </Paper>
      </Dialog>
      <AlertDialog alertState={dialogState} />
    </>
  );
};

export default CreatorNegotiateDialog;
